// src/components/padrekko/qc/QcFindingsLayer.jsx
//
// Renders the current pad's findings as colored polygons on the map.
// Panel-mounted (same lifecycle as QcInspector + QcPolygonDrawer): the
// layer exists only while the QC tab is active, so volumetrics + AOI
// modes never see leftover finding polygons.
//
// Color is data-driven from analysis.status, so a status flip from a
// reanalyze re-paints without re-adding layers:
//   ACCEPTABLE     emerald  (#10b981)
//   VERIFY         amber    (#f59e0b)
//   NON_COMPLIANT  red      (#ef4444)
//   fallback       slate    (#94a3b8)
//
// Four effects, in declaration order so React runs them top-to-bottom:
//
//   1. Mount/unmount: add source + fill + line on mount, remove on
//      unmount. Idempotent guards so React StrictMode's double-invoke
//      doesn't duplicate or 404. Cleanup removes layers first, then
//      source — MapLibre requires source-free state before removeSource.
//
//   2. Source data sync: setData on every findings array change. The
//      useMemo on featureCollection keeps reference equality stable when
//      findings haven't changed, so the effect won't fire spuriously.
//
//   3. Visibility toggle: drawingPolygon=true → visibility:'none'.
//      Critical: this is what keeps the canvas snapshot clean of prior
//      findings during draw (QcPolygonDrawer's snapshot sequence relies
//      on this flag being true through clear → idle → toDataURL).
//
//   4. Zoom-to-polygon: subscribes to useQcStore.zoomToPolygon. Fires
//      only when nonce changes (per-click), key-prefixed by padId so a
//      stale request from a different pad no-ops cleanly.

import { useEffect, useMemo } from "react";
import { useAppViewStore } from "../../../stores/useAppViewStore";
import { useQcStore, findingKey } from "../../../stores/useQcStore";

const SOURCE_ID    = "qc-findings";
const FILL_LAYER_ID = "qc-findings-fill";
const LINE_LAYER_ID = "qc-findings-line";

// Data-driven color expression. Reused for fill + line — the line is
// the same hue at full opacity so the outline reads cleanly over the
// translucent fill.
const STATUS_COLOR_MATCH = [
  "match",
  ["get", "status"],
  "ACCEPTABLE",    "#10b981",
  "VERIFY",        "#f59e0b",
  "NON_COMPLIANT", "#ef4444",
  /* fallback */   "#94a3b8",
];

// Day 4 — once the case reaches operator_review (and after), polygons color by
// the operator's remediation completion instead of analysis severity:
// completed → green, not_completed → amber, pending → red.
const COMPLETION_COLOR_MATCH = [
  "match",
  ["get", "completion_status"],
  "completed",     "#10b981",
  "not_completed", "#f59e0b",
  "pending",       "#ef4444",
  /* fallback */   "#ef4444",
];

function buildFeatureCollection(pad, dismissedFindingKeys, hiddenFindingKeys) {
  const findings = pad?.findings ?? [];
  const padId = pad?.id;
  return {
    type: "FeatureCollection",
    features: findings
      .filter((f) => f.polygon_geom?.type === "Polygon")
      .filter((f) => !dismissedFindingKeys?.has(findingKey(padId, f.idx)))
      .filter((f) => !hiddenFindingKeys?.has(findingKey(padId, f.idx)))
      .map((f) => ({
        type: "Feature",
        geometry: f.polygon_geom,
        properties: {
          idx: f.idx,
          // Default to VERIFY so a missing status (shouldn't happen,
          // but defensive) renders as amber rather than disappearing
          // off the fallback path.
          status: f.analysis?.status ?? "VERIFY",
          completion_status: f.completion_status ?? "pending",
        },
      })),
  };
}

export default function QcFindingsLayer() {
  const mapInstance          = useAppViewStore((s) => s.mapInstance);
  const session              = useQcStore((s) => s.session);
  const currentPadIdx        = useQcStore((s) => s.currentPadIdx);
  const drawingPolygon       = useQcStore((s) => s.drawingPolygon);
  const zoomToPolygon        = useQcStore((s) => s.zoomToPolygon);
  const dismissedFindingKeys = useQcStore((s) => s.dismissedFindingKeys);
  const hiddenFindingKeys    = useQcStore((s) => s.hiddenFindingKeys);
  const polygonEditMode      = useQcStore((s) => s.polygonEditMode);
  const currentStage         = useQcStore((s) => s.caseInfo?.current_stage ?? null);

  // Day 4 — at operator_review and after, color by completion; before, by
  // analysis severity (the chosen "completion only in operator-review+").
  const useCompletionColor = currentStage === "operator_review" || currentStage === "complete";

  const pad = session?.pads?.[currentPadIdx ?? 0];
  const padId = pad?.id;

  const featureCollection = useMemo(
    () => buildFeatureCollection(pad, dismissedFindingKeys, hiddenFindingKeys),
    // Keyed on findings array reference + the dismissed Set reference +
    // the hidden Set reference. All three are replaced immutably by their
    // respective mutators (analyzeFinding / dismissFinding /
    // toggleFindingVisibility), so reference equality is a valid change
    // signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pad?.findings, dismissedFindingKeys, hiddenFindingKeys]
  );

  // ── 1. mount/unmount: add layers + source, remove on cleanup ─────────
  useEffect(() => {
    if (!mapInstance) return;

    if (!mapInstance.getSource(SOURCE_ID)) {
      mapInstance.addSource(SOURCE_ID, {
        type: "geojson",
        data: { type: "FeatureCollection", features: [] },
      });
    }
    if (!mapInstance.getLayer(FILL_LAYER_ID)) {
      mapInstance.addLayer({
        id: FILL_LAYER_ID,
        type: "fill",
        source: SOURCE_ID,
        paint: {
          "fill-color": STATUS_COLOR_MATCH,
          "fill-opacity": 0.25,
        },
      });
    }
    if (!mapInstance.getLayer(LINE_LAYER_ID)) {
      mapInstance.addLayer({
        id: LINE_LAYER_ID,
        type: "line",
        source: SOURCE_ID,
        paint: {
          "line-color": STATUS_COLOR_MATCH,
          "line-width": 2,
        },
      });
    }

    return () => {
      // Layers first, then source — MapLibre rejects removeSource while
      // any layer still references it.
      if (mapInstance.getLayer(LINE_LAYER_ID)) mapInstance.removeLayer(LINE_LAYER_ID);
      if (mapInstance.getLayer(FILL_LAYER_ID)) mapInstance.removeLayer(FILL_LAYER_ID);
      if (mapInstance.getSource(SOURCE_ID))    mapInstance.removeSource(SOURCE_ID);
    };
  }, [mapInstance]);

  // ── 2. source data sync ──────────────────────────────────────────────
  useEffect(() => {
    if (!mapInstance) return;
    const src = mapInstance.getSource(SOURCE_ID);
    if (src) src.setData(featureCollection);
  }, [mapInstance, featureCollection]);

  // ── 2b. stage-driven recolor: severity (pre-operator) → completion (op+) ─
  useEffect(() => {
    if (!mapInstance) return;
    const expr = useCompletionColor ? COMPLETION_COLOR_MATCH : STATUS_COLOR_MATCH;
    if (mapInstance.getLayer(FILL_LAYER_ID)) mapInstance.setPaintProperty(FILL_LAYER_ID, "fill-color", expr);
    if (mapInstance.getLayer(LINE_LAYER_ID)) mapInstance.setPaintProperty(LINE_LAYER_ID, "line-color", expr);
  }, [mapInstance, useCompletionColor]);

  // ── 3. visibility toggle: hide during polygon draw ───────────────────
  useEffect(() => {
    if (!mapInstance) return;
    const visibility = drawingPolygon ? "none" : "visible";
    if (mapInstance.getLayer(FILL_LAYER_ID)) {
      mapInstance.setLayoutProperty(FILL_LAYER_ID, "visibility", visibility);
    }
    if (mapInstance.getLayer(LINE_LAYER_ID)) {
      mapInstance.setLayoutProperty(LINE_LAYER_ID, "visibility", visibility);
    }
  }, [mapInstance, drawingPolygon]);

  // ── 3b. polygon-edit dim (Day 8 Phase 2 · Flag γ1) ───────────────────
  // When polygon-edit mode is active for a finding on THIS pad, hide that
  // finding (MlDraw owns the rendering of the active edit) and dim all
  // other findings to focus attention. When not in edit mode, restore the
  // constant defaults (0.25 fill, 1 line).
  useEffect(() => {
    if (!mapInstance) return;
    const editingIdx = polygonEditMode?.findingIdx;
    const editingPadIdx = polygonEditMode?.padIdx;
    const padIdxNow = currentPadIdx ?? 0;
    // Only dim when the editing finding is on the currently-viewed pad.
    const active = editingIdx != null && editingPadIdx === padIdxNow;

    if (mapInstance.getLayer(FILL_LAYER_ID)) {
      if (active) {
        mapInstance.setPaintProperty(FILL_LAYER_ID, "fill-opacity", [
          "case",
          ["==", ["get", "idx"], editingIdx], 0,    // hide editing one (MlDraw renders it)
          0.05,                                       // dim others
        ]);
      } else {
        mapInstance.setPaintProperty(FILL_LAYER_ID, "fill-opacity", 0.25);
      }
    }
    if (mapInstance.getLayer(LINE_LAYER_ID)) {
      if (active) {
        mapInstance.setPaintProperty(LINE_LAYER_ID, "line-opacity", [
          "case",
          ["==", ["get", "idx"], editingIdx], 0,
          0.35,
        ]);
      } else {
        mapInstance.setPaintProperty(LINE_LAYER_ID, "line-opacity", 1);
      }
    }
  }, [mapInstance, polygonEditMode?.findingIdx, polygonEditMode?.padIdx, currentPadIdx]);

  // ── 4. zoom-to-polygon: fitBounds when nonce changes ─────────────────
  useEffect(() => {
    if (!mapInstance || !zoomToPolygon?.key || !padId) return;

    const expectedPrefix = `${padId}:`;
    if (!zoomToPolygon.key.startsWith(expectedPrefix)) return;

    const idx = parseInt(zoomToPolygon.key.slice(expectedPrefix.length), 10);
    if (!Number.isInteger(idx)) return;

    const finding = (pad?.findings ?? []).find((f) => f.idx === idx);
    const ring = finding?.polygon_geom?.coordinates?.[0];
    if (!ring?.length) return;

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    for (const [x, y] of ring) {
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }

    mapInstance.fitBounds(
      [[minX, minY], [maxX, maxY]],
      {
        // Tighter than pad-zoom — operator is honing in on one finding.
        padding: { top: 80, bottom: 80, left: 80, right: 80 },
        maxZoom: 19,
        duration: 500,
      }
    );
    // Fire only on nonce change. The other deps (padId, pad?.findings)
    // are read for the lookup but shouldn't retrigger the zoom — auto-
    // zoom-to-pad and source-data-sync own those signals respectively.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapInstance, zoomToPolygon?.nonce]);

  return null;
}
